import React, { useState, useRef, useEffect } from 'react';
import './table-search-icon.scss';
import {
  OutlinedInput,
  Popper,
  ClickAwayListener,
  Paper,
  InputAdornment
} from '@mui/material';
import {
  InputSearchIcon,
  CloseIcon
} from '@components/icons/svg-icon.tsx';
import { TableSearchIconProps } from '@models/table.model.ts';

const TableSearchIcon: React.FC<TableSearchIconProps> = ({
  columnId,
  value,
  onSearchChange,
  clearSearch
}) => {
  const [showInput, setShowInput] = useState(false);
  const [inputValue, setInputValue] = useState(value);
  const anchorRef = useRef<HTMLButtonElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  };

  const handleClearInput = () => {
    clearSearch?.(columnId);
    setTimeout(() => {
      inputRef.current?.focus();
    }, 0);
  };

  useEffect(() => {
    const trimmedValue =
      typeof inputValue === 'string' ? inputValue.trim() : '';

    const timeout = setTimeout(() => {
      if (value !== trimmedValue) {
        onSearchChange(columnId, trimmedValue);
      }
    }, 400);

    return () => clearTimeout(timeout);
  }, [inputValue, columnId]);

  useEffect(() => {
    setInputValue(value != null ? String(value) : '');
    if (value != null && value !== '') {
      setShowInput(true);
    }
  }, [value]);

  return (
    <div className="flex items-center">
      <button
        className="cursor-pointer"
        ref={anchorRef}
        onClick={() => setShowInput((prev) => !prev)}
      >
        <InputSearchIcon
          fill="var(--blue-4)"
          className="!w-[18px] !h-[18px] search-icon-hover max-lg-xl:!w-[16px] max-lg-xl:!h-[16px]"
        />
      </button>

      <Popper
        open={showInput}
        anchorEl={anchorRef.current}
        placement="bottom-start"
        sx={{
          zIndex: 1300,
          minWidth: 350,
        }}
        modifiers={[
          {
            name: 'offset',
            options: {
              offset: [0, 12]
            }
          }
        ]}
      >
        <ClickAwayListener onClickAway={() => setShowInput(false)}>
          <Paper className="!p-1.5 shadow-md">
            <OutlinedInput
              inputRef={inputRef}
              sx={{ minWidth: '308px', width: '100%' }}
              placeholder="Search..."
              value={inputValue}
              onChange={handleInputChange}
              autoFocus
              endAdornment={
                inputValue ? (
                  <InputAdornment position="end">
                    <button
                      className="cursor-pointer px-3"
                      onClick={handleClearInput}
                    >
                      <CloseIcon
                        stroke="var(--primary)"
                        className="!w-[14px] !h-[14px] -mr-2"
                      />
                    </button>
                  </InputAdornment>
                ) : null
              }
            />
          </Paper>
        </ClickAwayListener>
      </Popper>
    </div>
  );
};

export default TableSearchIcon;
